/**
 * chapter — shared scaffolding for Settings chapters.
 *
 * Settings cards and the Usage pages share one idiom: a UI-voice headline, a
 * short lede, then quiet mono section labels between groups. The section
 * label is the same voice Team settings uses for its section heads
 * (`.set-sec-l` in desk-settings.css), so the two settings surfaces read as
 * one system.
 *
 * Settings section titles are real headings (h2) so screen readers can
 * navigate the chapter, and the chapter title is the page's h1: the Triptych
 * shell chrome renders no h1 of its own.
 *
 * The chapter name already sits in the Triptych header and on the open rail
 * entry, so the header renders no kicker; the prop stays on the contract so
 * every chapter keeps naming itself in one place.
 */
import type { ReactNode } from "react";
import { Spinner } from "../ui/spinner.js";

/** The chapter title, in the display voice. */
function Headline({ children, size }: { children: ReactNode; size: number }) {
  return (
    <h1
      className="m-0"
      style={{
        fontFamily: "var(--display)",
        fontSize: size,
        lineHeight: 1.15,
        letterSpacing: "-0.018em",
        color: "var(--text-primary)",
      }}
    >
      {children}
    </h1>
  );
}

/** A short lede under a title or a section, in the body voice. */
function Dek({ children, size }: { children: ReactNode; size: number }) {
  return (
    <p
      className="m-0 mt-2"
      style={{ fontFamily: "var(--body)", fontSize: size, lineHeight: 1.55, color: "var(--text-secondary)" }}
    >
      {children}
    </p>
  );
}

const SECTION_LABEL_STYLE = {
  fontFamily: "var(--font-mono)",
  fontWeight: 500,
  fontSize: 10.5,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--text-label)",
} as const;

export function ChapterHeader({
  title,
  lede,
  actions,
}: {
  kicker: string;
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-1">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Headline size={22}>
            {title}
          </Headline>
          {lede && (
            <div className="max-w-[560px]">
              <Dek size={13.5}>{lede}</Dek>
            </div>
          )}
        </div>
        {actions && <div className="shrink-0 pt-1">{actions}</div>}
      </div>
    </header>
  );
}

/** Quiet mono rule between chapter sections. The label is an h2 so assistive
 * tech can jump section-to-section within a chapter. The running order is the
 * rail's job, not the heading's, so nothing prints a number here; `folio` is
 * still accepted (and ignored) so older call sites that pass a literal keep
 * compiling until they are touched. */
export function ChapterSection({
  label,
  rightLabel,
}: {
  folio?: string;
  label: string;
  rightLabel?: string;
}) {
  return (
    <div
      className="chap-sec flex items-baseline justify-between gap-3 mt-9 mb-4"
      style={{ borderBottom: "1px solid var(--border-subtle)", paddingBottom: 8 }}
    >
      <h2 className="m-0" style={SECTION_LABEL_STYLE}>
        {label}
      </h2>
      {rightLabel && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            letterSpacing: "0.06em",
            color: "var(--text-label)",
            whiteSpace: "nowrap",
          }}
        >
          {rightLabel}
        </span>
      )}
    </div>
  );
}

/** Short explanatory paragraph that opens a section. */
export function ChapterLede({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4 max-w-[560px]">
      <Dek size={13}>{children}</Dek>
    </div>
  );
}

/** The chapter card. Print material: a committed, quiet group on the panel. */
export function ChapterCard({
  children,
  className = "",
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`rounded-[var(--radius-card)] border border-[var(--print-border)] bg-[var(--print-bg)] shadow-[var(--shadow-print)] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

/** Centered loading state that stays inside the panel (no full-bleed wash).
 * With a visible label the spinner is decorative (aria-hidden) so screen
 * readers hear the label once, not twice. */
export function ChapterLoading({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16">
      {label ? (
        <Spinner size="sm" aria-hidden="true" />
      ) : (
        <Spinner size="sm" aria-label="Loading" />
      )}
      {label && (
        <span className="text-sm text-[var(--text-tertiary)]">{label}</span>
      )}
    </div>
  );
}

export function ChapterError({ children }: { children: ReactNode }) {
  return (
    <p className="py-16 text-center text-sm text-[var(--error)]" role="alert">
      {children}
    </p>
  );
}
